import React, { useMemo, useRef, useState } from 'react';
import { Button, Typography } from '@getgo/chameleon-web/react';
import { Form, Formik, FormikValues } from 'formik';
import { useTranslation, Trans } from 'react-i18next';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useRegistrationContext } from '../../context/registrationContext';
import { useEventContext } from '../../context/eventContext';
import { useHistory } from 'react-router-dom';
import RegFormContainer from './form/regFormContainer';
import {
  createRegistrationSubmissionObj,
  getFormInitialValuesWithQuestionnaireInitialValues,
  getQuestionNameByQuestionDetailsMap,
  getRequiredFieldByDetailsMap,
  getValidationSchemaForRegForm,
  sortedEntries,
  createMixPanelData,
} from './registrationhelper/helper';
import setMixPanelProperties from '../../../mixpanel/setMixPanelProperties';
import {
  onFailureRegisterUserTracker,
  onRegisterUserTracker,
  onSuccessRegisterUserTracker,
} from '../../../mixpanel/Feature/registerUserTracker';
import { setCookie } from '../../utils/browserCookies';
import FormatStringUrl from '../../utils/formatStringUrl';
import API from '../../utils/api';
import { IconButton } from '@getgo/chameleon-web/react';
import { CloseIcon } from '@getgo/chameleon-icons/react';
import '../styles/events.scss';
import EventHostedByDetails from './eventHostedByDetails';
import { FormikHelpers } from 'formik/dist/types';
import '../styles/event-registration-form.scss';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
} from '@stripe/react-stripe-js';
import visa from '../../../images/visa.png';
import masterCard from '../../../images/masterCard.png';
import amex from '../../../images/amex.png';
import discover from '../../../images/discover.jpg';
import { LockedInactiveIcon } from '@getgo/chameleon-icons/react';
import { loadStripe } from '@stripe/stripe-js';
import { apiStatus } from '../../../utils/constants';
import { IPaymentMethodResult } from '../eventInterface';

const noOfDefaultQuestion = 3;

const APPROVAL_TYPE = {
  MANUAL: 'MANUAL',
  AUTOMATIC: 'AUTOMATIC',
};

interface EventRegistration {
  closeForm(): void;
  openForm(div: HTMLDivElement | null): void;
  isFormVisible: boolean;
  hideHCaptcha: boolean;
}

const EventRegistrationForm = ({
  closeForm,
  isFormVisible,
  openForm,
  hideHCaptcha,
}: EventRegistration) => {
  const isMobOrTabView = window.matchMedia('(max-width: 1024px)').matches;
  const { t } = useTranslation();
  const divRef = useRef<HTMLDivElement>(null);
  const regFormRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { setRegisteredUserDetails } = useRegistrationContext();
  const { eventDetails, setEventDetails } = useEventContext();
  const captchaRef = useRef<HCaptcha>(null);
  const [card, setCard] = useState({
    cardNumber: { event: null, element: null },
    cardExpiry: { event: null, element: null },
    cardCvc: { event: null, element: null },
  } as any);
  const [paymentError, setPaymentError] = useState(false);
  const stripe = useStripe();

  let initialValues: any = {
    firstName: '',
    lastName: '',
    email: '',
    isDisclaimerChecked: false,
  };
  let fieldNameByDetailsMap: Map<string, any> | null = null;
  let templateQuestionNameByDetailsMap: Map<string, any> | null = null;
  let customQuestionNameByDetailsMap: Map<string, any> | null = null;

  const questionnaireDetails = eventDetails?.questionnaireDetails;
  const questions = questionnaireDetails?.questions;
  const requiredFieldNameByDetailsMap = useMemo(() => getRequiredFieldByDetailsMap(t), [t]);

  if (questionnaireDetails && questions) {
    initialValues = getFormInitialValuesWithQuestionnaireInitialValues(initialValues, questions);

    templateQuestionNameByDetailsMap = getQuestionNameByQuestionDetailsMap(
      questions,
      (question) => question.readOnly,
    );
    customQuestionNameByDetailsMap = getQuestionNameByQuestionDetailsMap(
      questions,
      (question) => !question.readOnly,
      'customQuestion',
    );
  }

  const validationSchemaForRegForm = (() => {
    fieldNameByDetailsMap = new Map([...requiredFieldNameByDetailsMap]);

    if (templateQuestionNameByDetailsMap) {
      fieldNameByDetailsMap = new Map([
        ...fieldNameByDetailsMap,
        ...templateQuestionNameByDetailsMap,
      ]);
    }

    if (customQuestionNameByDetailsMap) {
      fieldNameByDetailsMap = new Map([
        ...fieldNameByDetailsMap,
        ...customQuestionNameByDetailsMap,
      ]);
    }

    return getValidationSchemaForRegForm(fieldNameByDetailsMap, questionnaireDetails);
  })();

  const openMobForm = (actions: FormikHelpers<any>) => {
    openForm(regFormRef.current);
    actions.setTouched({});
  };

  const triggerCaptcha = async (values: FormikValues, actions: FormikHelpers<any>) => {
    if (!isFormVisible) return;
    if (
      eventDetails?.paymentDetails &&
      eventDetails?.paymentDetails?.amount &&
      parseInt(eventDetails?.paymentDetails.amount, 10) !== 0 &&
      !validateCardDetails()
    )
      return;
    actions.setSubmitting(true);
    if (values.humanRegistrantToken) return await registerAttendee(values, actions);

    if (!hideHCaptcha) {
      const { response } = (await captchaRef?.current?.execute({ async: true })) ?? {};
      if (response) await registerAttendee({ ...values, humanRegistrantToken: response }, actions);
    } else {
      await registerAttendee({ ...values }, actions);
    }

    actions.setSubmitting(false);
  };

  const registerAttendee = async (values: FormikValues, actions: FormikHelpers<any>) => {
    const eventKey = eventDetails?.eventKey;

    const userDetails: any = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
    };

    onRegisterUserTracker({
      eventKey: eventDetails?.eventKey,
      userDetails: values,
      description: 'Registering For The Event',
    });
    const registrationSubmissionReq = createRegistrationSubmissionObj(
      fieldNameByDetailsMap,
      questionnaireDetails,
      values,
    );
    const mixpanelData = createMixPanelData(questionnaireDetails, values, fieldNameByDetailsMap);
    let totalNoOfQuestion = noOfDefaultQuestion;
    let totalnoOfTemplateQuestion = 0;
    let totalnoOfCustomQuestion = 0;
    let noOfNonMandatoryQuestion: any = [];
    let noOfNonMandatoryQuestionAnswered = [];
    let noOfNonMandatoryQuestionAnsweredInPercent = 0;

    if (questionnaireDetails && questionnaireDetails.questions) {
      totalNoOfQuestion = totalNoOfQuestion + questionnaireDetails?.questions.length;
      totalnoOfTemplateQuestion = questionnaireDetails?.questions.filter(
        (question) => question.readOnly,
      ).length;
      totalnoOfCustomQuestion = questionnaireDetails?.questions.filter(
        (question) => !question.readOnly,
      ).length;
      noOfNonMandatoryQuestion = questionnaireDetails?.questions.filter(
        (question) => !question.required,
      );
      noOfNonMandatoryQuestionAnswered =
        registrationSubmissionReq.questionnaireSubmission.answers.filter(
          (answer: any) =>
            noOfNonMandatoryQuestion &&
            noOfNonMandatoryQuestion?.findIndex(
              (question: any) => question.questionKey === answer.questionKey,
            ) > -1,
        );
      noOfNonMandatoryQuestionAnsweredInPercent =
        noOfNonMandatoryQuestion &&
        noOfNonMandatoryQuestion.length &&
        (noOfNonMandatoryQuestionAnswered.length / noOfNonMandatoryQuestion.length) * 100;
    }

    try {
      if (eventDetails?.paymentDetails && eventDetails?.paymentDetails.amount) {
        let paymentMethodResult: IPaymentMethodResult | null = null;
        if (parseInt(eventDetails?.paymentDetails.amount) !== 0) {
          paymentMethodResult = await generatePaymentMethod(userDetails);
        }
        registrationSubmissionReq.paymentDetails = {
          paymentProvider: 'STRIPE',
          paymentMethodId: paymentMethodResult ? paymentMethodResult?.paymentMethod?.id : '',
          couponKey: promoCode.couponKey,
        };
      }
      const response = await API.sendRegistrationDetails(eventKey, registrationSubmissionReq);

      if (response.status === 201) {
        const { userKey, eventRegistrationId } = response.data;
        userDetails['userKey'] = userKey;
        userDetails['eventRegistrationId'] = eventRegistrationId;
        setRegisteredUserDetails?.(userDetails);

        setMixPanelProperties(userDetails, eventKey, eventRegistrationId);

        onSuccessRegisterUserTracker(
          {
            eventKey,
            userDetails: mixpanelData,
          },
          noOfNonMandatoryQuestion?.length,
          noOfNonMandatoryQuestionAnswered.length,
          noOfNonMandatoryQuestionAnsweredInPercent,
          totalNoOfQuestion,
          totalnoOfTemplateQuestion,
          totalnoOfCustomQuestion,
          eventDetails?.paymentDetails && eventDetails?.paymentDetails?.amount
            ? `${eventDetails?.paymentDetails?.amount} ${eventDetails?.paymentDetails?.currency}`
            : null,
          promoCode?.couponKey ? true : false,
          'Succesfully Registered For The Event',
        );
        captchaRef?.current?.resetCaptcha();
        actions.setSubmitting(false);
        closeForm();
        if (eventDetails?.approvalType === APPROVAL_TYPE.AUTOMATIC) {
          setCookie(eventKey, JSON.stringify(userDetails), eventDetails?.end, 7);
          history.push(`/successregistration/${eventKey}`);
        } else {
          history.push(`/pendingapproval/${eventKey}/${eventRegistrationId}`);
        }
      }
    } catch (e: any) {
      if (e?.response?.status === apiStatus.notAcceptable) {
        history.push(`/declinedregistration/${eventKey}`);
      }
      //start handle 3d flow of payment
      if (e?.response?.status === apiStatus.paymentInProcess) {
        history.push(`/paymentinprocess/${eventDetails?.eventKey}`);
        const eventRegistrationId = e?.response?.data.eventRegistrationId;
        const userKey = e?.response?.data.userKey;
        userDetails['userKey'] = eventRegistrationId;
        userDetails['eventRegistrationId'] = userKey;
        setRegisteredUserDetails?.(userDetails);
        const stripeData = {
          clientSecret: e?.response?.data.paymentResponse.paymentIntentSecretKey,
          stripeAccountKey: e?.response?.data.paymentResponse.paymentProviderReferenceId,
        };
        try {
          const paymentConfirmationResult: any = await confirmingCardPayment(stripeData);
          const navigateTo = paymentConfirmationResult.paymentIntent
            ? eventDetails?.approvalType === APPROVAL_TYPE.AUTOMATIC
              ? `successregistration/${eventDetails?.eventKey}`
              : `pendingapproval/${eventDetails?.eventKey}/${eventRegistrationId}`
            : `paymentauthfailed/${eventDetails?.eventKey}`;
          history.push(`/${navigateTo}`);
        } catch (error: any) {
          const navigateTo =
            error.error.code === 'payment_intent_authentication_failure'
              ? 'paymentauthfailed'
              : 'paymentauthfailed';
          history.push(`/${navigateTo}/${eventDetails?.eventKey}`);
        }
      }
      //End handle 3d flow of payment
      onFailureRegisterUserTracker(
        {
          eventKey,
          userDetails: mixpanelData,
        },
        noOfNonMandatoryQuestion?.length,
        noOfNonMandatoryQuestionAnswered.length,
        noOfNonMandatoryQuestionAnsweredInPercent,
        totalNoOfQuestion,
        totalnoOfTemplateQuestion,
        totalnoOfCustomQuestion,
        'Failed To Register For The Event',
      );
      captchaRef?.current?.resetCaptcha();
      actions.setSubmitting(false);
      onFailureRegisterUserTracker({
        eventKey: eventDetails?.eventKey,
        userDetails: values,
        description: 'Failed To Register For The Event',
      });
    }
  };

  const totalFields = fieldNameByDetailsMap?.size;
  const elementStyle = {
    style: {
      base: {
        iconColor: '#555F72',
        color: '#555555',

        '::placeholder': {
          color: '#cccccc',
        },
        ':-webkit-autofill': {
          color: '#cccccc',
        },
      },
      invalid: {
        iconColor: '#555555',
        color: '#555555',
      },
    },
  };
  const generatePaymentMethod = (data: any): Promise<IPaymentMethodResult> => {
    return new Promise(function (resolve, reject) {
      const additionalData = {
        //get this data from page
        firstname: (data && data.firstName) || undefined,
        lastname: (data && data.lastName) || undefined,
        email: (data && data.email) || undefined,
      };

      // If first name, last name and email fields are not empty only then proceed
      if (additionalData.firstname && additionalData.lastname && additionalData.email) {
        stripe
          ?.createPaymentMethod({
            type: 'card',
            card: card.cardNumber.element,
            billing_details: {
              name: additionalData.firstname + ' ' + additionalData.lastname,
              email: additionalData.email,
            },
          })
          .then(function (result: any) {
            // Handle result.error or result.paymentMethod
            if (result.paymentMethod) {
              resolve(result);
            } else {
              reject(result);
            }
          })
          .catch(function (err: any) {
            reject(err);
          });
      } else {
        reject(null);
      }
    });
  };
  const confirmingCardPayment = async function (stripeData: any) {
    /* jshint ignore:start */
    const newStripe: any = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISH_KEY || '', {
      stripeAccount: stripeData.stripeAccountKey,
      apiVersion: process.env.REACT_APP_STRIPE_VERSION,
    });
    /* jshint ignore:end */
    return new Promise(function (resolve, reject) {
      newStripe
        .confirmCardPayment(stripeData.clientSecret)
        .then(function (result: any) {
          if (result.paymentIntent) {
            resolve(result);
          } else {
            reject(result);
          }
        })
        .catch(function (err: any) {
          reject(err);
        });
    });
  };
  const validateCardDetails = () => {
    let emptyInputField = false;
    for (const cardElement in card) {
      if (card[cardElement].element._empty === true) {
        card[cardElement].element._parent.classList.add('StripeElement--invalid');
        card[cardElement].event = { error: { message: t(`payment.${cardElement}Required`) } };
        emptyInputField = true;
      }
    }
    if (emptyInputField) {
      setPaymentError(true);
      return false;
    }
    setPaymentError(false);
    return true;
  };
  const [promoCode, setPromoCode] = useState({ code: '', couponKey: '', touched: false });
  const onChangeCardDetails = (event: any) => {
    card[event.elementType].event = event;
    setCard({ ...card });
  };

  const onReadyCardDetails = (element: any) => {
    card[element._componentName].element = element;
    setCard(card);
  };

  const setInvalidPromoDetails = () => {
    if (eventDetails) {
      const newEventDetails = {
        ...eventDetails,
        paymentDetails: {
          ...eventDetails['paymentDetails'],
          amount: eventDetails?.paymentDetailsResponse?.amount,
        },
      };
      setEventDetails(newEventDetails);
    }
    setPromoCode({ ...promoCode, couponKey: '', touched: true });
  };

  const applyPromoCode = async () => {
    const alphanumericRegex = /^[a-z0-9]+$/i;
    if (promoCode && alphanumericRegex.test(promoCode.code)) {
      const payload = {
        promoCode: promoCode.code,
        eventKey: eventDetails?.eventKey,
      };
      try {
        const response = await API.validateCoupon(payload);
        const { value, couponKey } = response?.data;
        if (eventDetails) {
          const newEventDetails = {
            ...eventDetails,
            paymentDetails: { ...eventDetails['paymentDetails'], amount: value },
          };
          setEventDetails(newEventDetails);
          setPromoCode({ ...promoCode, couponKey, touched: true });
        }
      } catch (e) {
        setInvalidPromoDetails();
      }
    } else {
      setInvalidPromoDetails();
    }
  };

  const hasPaymentError = () => {
    return !(
      card?.cardNumber?.event?.complete &&
      card?.cardExpiry?.event?.complete &&
      card?.cardCvc?.event?.complete
    );
  };

  return (
    <>
      {isFormVisible && <div className="overlay" />}
      <div className={isFormVisible ? 'event-registration-form' : ''} ref={divRef}>
        <Formik
          initialValues={initialValues}
          onSubmit={triggerCaptcha}
          validationSchema={isFormVisible && validationSchemaForRegForm}
        >
          {(formikProps) => {
            return (
              <Form>
                <div
                  className={`event-registration-form-fixed ${
                    isFormVisible ? 'open-modal' : 'close-modal'
                  }`}
                >
                  <div className="registrationHeader">
                    <Typography variant="heading-small" color="text-01" className="form-heading">
                      {eventDetails?.paymentDetails &&
                      typeof eventDetails?.paymentDetails?.amount == 'string' &&
                      parseInt(eventDetails?.paymentDetails?.amount, 10) !== 0
                        ? t('payment.registerAmount', {
                            amount: `${eventDetails?.paymentDetails?.amount} ${eventDetails?.paymentDetails?.currency}`,
                          })
                        : t('event.registration.title')}
                    </Typography>
                    {isMobOrTabView && (
                      <IconButton variant="neutral" onClick={closeForm}>
                        <CloseIcon />
                      </IconButton>
                    )}
                  </div>
                  <div className="registrationForm" ref={regFormRef}>
                    {requiredFieldNameByDetailsMap &&
                      Array.from(requiredFieldNameByDetailsMap).map(([name, details]) => {
                        const { ...restDetails } = details;
                        return (
                          <RegFormContainer
                            key={name}
                            name={name}
                            placeholder={t(`event.form.${name}Placeholder`)}
                            noOfFields={totalFields}
                            {...restDetails}
                          />
                        );
                      })}
                    {templateQuestionNameByDetailsMap &&
                      sortedEntries(templateQuestionNameByDetailsMap).map(([name, details]) => {
                        const {
                          questionKey, // eslint-disable-line @typescript-eslint/no-unused-vars
                          sortOrder, // eslint-disable-line @typescript-eslint/no-unused-vars
                          questionType, // eslint-disable-line @typescript-eslint/no-unused-vars
                          isTemplateQuestion, // eslint-disable-line @typescript-eslint/no-unused-vars
                          ...restDetails
                        } = details;
                        return (
                          <RegFormContainer
                            key={name}
                            name={name}
                            noOfFields={totalFields}
                            placeholder={t(`event.form.${name}Placeholder`)}
                            {...restDetails}
                          />
                        );
                      })}
                    {customQuestionNameByDetailsMap &&
                      sortedEntries(customQuestionNameByDetailsMap).map(([name, details]) => {
                        const {
                          questionKey, // eslint-disable-line @typescript-eslint/no-unused-vars
                          sortOrder, // eslint-disable-line @typescript-eslint/no-unused-vars
                          questionType, // eslint-disable-line @typescript-eslint/no-unused-vars
                          isTemplateQuestion, // eslint-disable-line @typescript-eslint/no-unused-vars
                          ...restDetails
                        } = details;
                        return (
                          <RegFormContainer
                            key={name}
                            name={name}
                            noOfFields={totalFields}
                            placeholder={t(`event.form.customQuestionPlaceholder`)}
                            {...restDetails}
                          />
                        );
                      })}
                    <div className="reqiured-info">
                      <Typography color="text-02" variant="caption-default-01">
                        {t('event.form.requiredInfo')}
                      </Typography>
                      <hr className="form-separator" />
                    </div>

                    {eventDetails?.paymentDetails && (
                      <div className="payment-body" id="payment-form">
                        <div className="paymentFields">
                          {eventDetails?.paymentDetails &&
                            typeof eventDetails?.paymentDetails?.amount == 'string' &&
                            parseInt(eventDetails?.paymentDetails.amount, 10) !== 0 && (
                              <div>
                                <div className="card-disclaimer">
                                  <div style={{ display: 'flex', marginBottom: '8px' }}>
                                    <LockedInactiveIcon />
                                    <Typography
                                      variant="caption-small-02"
                                      style={{ paddingTop: '4px' }}
                                    >
                                      {t('payment.disclaimer')}
                                    </Typography>
                                  </div>
                                  <div>
                                    <span className="cards-icons">
                                      <img src={visa}></img>
                                      <img src={masterCard}></img>
                                      <img src={amex}></img>
                                      <img src={discover}></img>
                                    </span>
                                  </div>
                                </div>
                                <div className="field card-number">
                                  <Typography
                                    tag="label"
                                    className="control-label"
                                    variant="caption-default-02"
                                    color="text-01"
                                  >
                                    {t('payment.cardNumberLabel')}
                                    {'*'}
                                  </Typography>
                                  <CardNumberElement
                                    className="field empty input payment-fields"
                                    id="card-number"
                                    options={elementStyle}
                                    onReady={(el) => onReadyCardDetails(el)}
                                    onChange={(e) => onChangeCardDetails(e)}
                                  />
                                  {card?.cardNumber?.event?.error && (
                                    <Typography
                                      color="text-error-01"
                                      variant="caption-small-02"
                                      style={{ marginTop: '5px' }}
                                    >
                                      {card?.cardNumber?.event?.error?.message}
                                    </Typography>
                                  )}
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <div className="field card-expiry" style={{ width: '48%' }}>
                                    <Typography
                                      tag="label"
                                      className="control-label"
                                      variant="caption-default-02"
                                      color="text-01"
                                    >
                                      {t('payment.cardExpiryLabel')}
                                      {'*'}
                                    </Typography>
                                    <CardExpiryElement
                                      id="card-expiry"
                                      className="field empty input payment-fields"
                                      options={elementStyle}
                                      onChange={(e) => onChangeCardDetails(e)}
                                      onReady={(el) => onReadyCardDetails(el)}
                                    />
                                    {card?.cardExpiry?.event?.error && (
                                      <Typography
                                        color="text-error-01"
                                        variant="caption-small-02"
                                        style={{ marginTop: '5px' }}
                                      >
                                        {card?.cardExpiry?.event?.error?.message}
                                      </Typography>
                                    )}
                                  </div>

                                  <div className="field card-cvc" style={{ width: '48%' }}>
                                    <Typography
                                      tag="label"
                                      className="control-label"
                                      variant="caption-default-02"
                                      color="text-01"
                                    >
                                      {t('payment.cardCvcLabel')}
                                      {'*'}
                                    </Typography>
                                    <CardCvcElement
                                      id="card-cvc"
                                      className="field empty input payment-fields"
                                      options={elementStyle}
                                      onChange={(e) => onChangeCardDetails(e)}
                                      onReady={(el) => onReadyCardDetails(el)}
                                    />
                                    {card?.cardCvc?.event?.error && (
                                      <Typography
                                        color="text-error-01"
                                        variant="caption-small-02"
                                        style={{ marginTop: '5px' }}
                                      >
                                        {card?.cardCvc?.event?.error?.message}
                                      </Typography>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          {eventDetails?.couponExists?.exists && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <div>
                                <RegFormContainer
                                  name="DiscountCode"
                                  controlType="Input"
                                  key="DiscountCode"
                                  title={t('payment.discountCodeLabel')}
                                  placeholder={t('payment.discountCodePlaceholder')}
                                  onChange={(e) => {
                                    setPromoCode({
                                      ...promoCode,
                                      code: e.target.value,
                                      touched: false,
                                      couponKey: '',
                                    });
                                  }}
                                />
                                {promoCode.touched && !promoCode.couponKey && (
                                  <Typography
                                    color="text-error-01"
                                    variant="caption-default-01"
                                    style={{ marginTop: '-8px', marginBottom: '8px' }}
                                  >
                                    {t('payment.inValidDiscountCode')}
                                  </Typography>
                                )}
                                {promoCode.touched && promoCode.couponKey && (
                                  <Typography
                                    color="text-success-01"
                                    variant="caption-default-01"
                                    style={{ marginTop: '-8px', marginBottom: '8px' }}
                                  >
                                    {t('payment.validDiscountCode')}
                                  </Typography>
                                )}
                              </div>
                              <Button
                                style={{ justifyContent: 'center', alignItems: 'center' }}
                                variant="neutral"
                                size="medium"
                                fullwidth={isMobOrTabView}
                                onClick={applyPromoCode}
                              >
                                {t('payment.applyDiscountCode')}
                              </Button>
                            </div>
                          )}
                          <Typography
                            className="payment-disclaimer"
                            variant="caption-default-01"
                            color="text-02"
                          >
                            <Trans i18nKey={'payment.paymentDisclaimer'}>
                              NOTE: Please direct all payment-related questions to the event
                              organizer. Your registration for this event is subject to GoTo’s
                              <a
                                href="https://www.logmeininc.com/legal/terms-and-conditions"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Terms of Service
                              </a>
                              and
                              <a
                                href="https://www.logmeininc.com/legal/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Privacy Policy
                              </a>
                              .
                            </Trans>
                          </Typography>
                          <hr className="form-separator" />
                        </div>
                      </div>
                    )}

                    {questionnaireDetails?.customDisclaimerEnabled ? (
                      <RegFormContainer name="isDisclaimerChecked" controlType="Checkbox">
                        <FormatStringUrl>{questionnaireDetails.disclaimerText}</FormatStringUrl>
                        <Typography className="inline-block" color="text-02">
                          {t('event.form.required')}
                        </Typography>
                      </RegFormContainer>
                    ) : (
                      <Typography
                        className="text-disclaimer"
                        color="text-02"
                        variant="caption-default-01"
                      >
                        {t('event.form.disclaimerCheckboxLabel')}
                      </Typography>
                    )}
                    {eventDetails?.paymentDetails && (
                      <>
                        <hr className="form-separator" />
                        <div className="payment-re-registering-note">
                          <Typography color="text-02" variant="caption-default-01">
                            {`If you're re-registering, you will not be charged a second time.`}
                          </Typography>
                        </div>
                      </>
                    )}

                    {!hideHCaptcha && (
                      <div className="h-captcha">
                        <HCaptcha
                          size="invisible"
                          onVerify={(token) =>
                            formikProps.setFieldValue('humanRegistrantToken', token)
                          }
                          sitekey={process.env.REACT_APP_HCAPTCHA_KEY as string}
                          onExpire={() => formikProps.setFieldValue('humanRegistrantToken', null)}
                          id="h-captcha-input"
                          ref={captchaRef}
                        />
                      </div>
                    )}

                    {isMobOrTabView && eventDetails?.organizers?.length && (
                      <EventHostedByDetails eventDetail={eventDetails} />
                    )}
                  </div>
                </div>
                <div className="register-button-background">
                  {isFormVisible ? (
                    <Button
                      size="large"
                      type="submit"
                      disabled={
                        !formikProps.isValid ||
                        formikProps.isSubmitting ||
                        (eventDetails?.paymentDetails &&
                          typeof eventDetails?.paymentDetails?.amount == 'string' &&
                          parseInt(eventDetails?.paymentDetails?.amount) !== 0 &&
                          paymentError &&
                          hasPaymentError())
                      }
                      fullwidth={isMobOrTabView}
                    >
                      {eventDetails?.paymentDetails && eventDetails?.paymentDetails?.amount
                        ? t('payment.payAndRegister', {
                            amount: `${eventDetails?.paymentDetails?.amount} ${eventDetails?.paymentDetails?.currency}`,
                          })
                        : t('event.form.registerToAttend')}
                    </Button>
                  ) : (
                    <Button size="large" onClick={() => openMobForm(formikProps)} fullwidth>
                      {eventDetails?.paymentDetails && eventDetails?.paymentDetails?.amount
                        ? t('payment.payAndRegister', {
                            amount: `${eventDetails?.paymentDetails?.amount} ${eventDetails?.paymentDetails?.currency}`,
                          })
                        : t('event.form.registerToAttend')}
                    </Button>
                  )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
};

export default EventRegistrationForm;
